import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_API_URL}/transfers`;

interface TransferFile {
  id: number;
  file_name: string;
  mime_type: string | null;
  file_size: number;
  download_count: number;
  proxy_url: string;
}

interface Transfer {
  id: number;
  code: string;
  title: string | null;
  message: string | null;
  creator_name: string | null;
  expires_at: string | null;
  max_downloads: number;
  download_count: number;
  files: TransferFile[];
}

function formatFileSize(bytes: number): string {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function getMimeIcon(mime: string | null, filename: string): { bg: string; color: string; ext: string } {
  const ext = filename.split('.').pop()?.toUpperCase() || 'FILE';
  if (!mime) return { bg: '#f3f4f6', color: '#6b7280', ext };
  if (mime.startsWith('image/')) return { bg: '#dbeafe', color: '#1d4ed8', ext };
  if (mime.includes('pdf')) return { bg: '#fee2e2', color: '#dc2626', ext };
  if (mime.includes('word') || mime.includes('document')) return { bg: '#dbeafe', color: '#1d4ed8', ext };
  if (mime.includes('sheet') || mime.includes('excel')) return { bg: '#dcfce7', color: '#15803d', ext };
  if (mime.includes('presentation') || mime.includes('powerpoint')) return { bg: '#fef3c7', color: '#d97706', ext };
  if (mime.includes('zip') || mime.includes('rar') || mime.includes('archive')) return { bg: '#f3e8ff', color: '#7c3aed', ext };
  if (mime.startsWith('video/')) return { bg: '#fce7f3', color: '#be185d', ext };
  if (mime.startsWith('audio/')) return { bg: '#ecfdf5', color: '#059669', ext };
  return { bg: '#f3f4f6', color: '#6b7280', ext };
}

function formatExpiry(expires_at: string | null): string | null {
  if (!expires_at) return null;
  const d = new Date(expires_at);
  const diff = Math.ceil((d.getTime() - Date.now()) / 86400000);
  if (diff <= 0) return 'Bugün son gün';
  if (diff === 1) return '1 gün sonra silinir';
  return `${diff} gün sonra silinir`;
}

interface Props {
  code: string;
}

const PublicTransferView: React.FC<Props> = ({ code }) => {
  const [transfer, setTransfer] = useState<Transfer | null>(null);
  const [error, setError] = useState<{ message: string; expired?: boolean; limitReached?: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [hoveredFile, setHoveredFile] = useState<number | null>(null);

  useEffect(() => {
    axios
      .get(`${API}/public/${code}`)
      .then((r) => setTransfer(r.data))
      .catch((e) => {
        const d = e.response?.data;
        setError({ message: d?.message || 'Transfer bulunamadı', expired: d?.expired, limitReached: d?.limitReached });
      })
      .finally(() => setLoading(false));
  }, [code]);

  const handleFileDownload = (file: TransferFile) => {
    setDownloadingId(file.id);
    axios.post(`${API}/public/${code}/files/${file.id}/download`).catch(() => {});
    const a = document.createElement('a');
    a.href = file.proxy_url;
    a.download = file.file_name;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => setDownloadingId(null), 2500);
  };

  const handleDownloadAll = () => {
    setDownloadingAll(true);
    window.open(`${API}/public/${code}/download-all`, '_blank');
    setTimeout(() => setDownloadingAll(false), 3000);
  };

  const spinKeyframes = `
    @keyframes wt-spin { to { transform: rotate(360deg); } }
    @keyframes wt-fade-in { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
    * { box-sizing: border-box; }
  `;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{spinKeyframes}</style>
        <div style={{ width: 40, height: 40, border: '3px solid #e0e0e0', borderTopColor: '#333', borderRadius: '50%', animation: 'wt-spin 0.7s linear infinite' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#f7f7f5',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          padding: 20,
        }}
      >
        <style>{spinKeyframes}</style>
        {/* Logo */}
        <div style={{ marginBottom: 48 }}>
          <span style={{ fontWeight: 900, fontSize: 22, letterSpacing: -0.5, color: '#333' }}>AY-CRM</span>
          <span style={{ fontWeight: 400, fontSize: 22, letterSpacing: -0.5, color: '#333' }}> Transfer</span>
        </div>
        <div style={{ background: '#fff', borderRadius: 16, padding: '48px 40px', maxWidth: 400, width: '100%', textAlign: 'center', boxShadow: '0 2px 24px rgba(0,0,0,.06)' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: error.expired ? '#fef3c7' : '#fee2e2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: 28,
            }}
          >
            {error.expired ? '⏰' : error.limitReached ? '🚫' : '🔍'}
          </div>
          <h2 style={{ fontWeight: 700, fontSize: 20, color: '#111', marginBottom: 10 }}>
            {error.expired ? 'Bu transfer süresi doldu' : error.limitReached ? 'İndirme limiti aşıldı' : 'Transfer bulunamadı'}
          </h2>
          <p style={{ color: '#888', fontSize: 14, lineHeight: 1.6, margin: 0 }}>{error.message}</p>
        </div>
      </div>
    );
  }

  if (!transfer) return null;

  const totalSize = transfer.files.reduce((s, f) => s + f.file_size, 0);
  const expiryText = formatExpiry(transfer.expires_at);
  const senderName = transfer.creator_name || 'Biri';

  return (
    <div style={{ minHeight: '100vh', background: '#f7f7f5', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', display: 'flex', flexDirection: 'column' }}>
      <style>{spinKeyframes}</style>

      {/* Nav */}
      <header style={{ padding: '20px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <span style={{ fontWeight: 900, fontSize: 18, letterSpacing: -0.5, color: '#333' }}>AY-CRM</span>
          <span style={{ fontWeight: 400, fontSize: 18, letterSpacing: -0.5, color: '#333' }}> Transfer</span>
        </div>
      </header>

      {/* Main */}
      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 16px 60px' }}>
        <div style={{ width: '100%', maxWidth: 560, animation: 'wt-fade-in 0.4s ease' }}>
          {/* Sender info */}
          <div style={{ marginBottom: 24, textAlign: 'center' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                {senderName.charAt(0).toUpperCase()}
              </div>
              <span style={{ fontSize: 14, color: '#444', fontWeight: 500 }}>
                <strong style={{ color: '#111' }}>{senderName}</strong> size dosya gönderdi
              </span>
            </div>
            {transfer.title && transfer.title !== `Transfer #${transfer.id}` && (
              <h1 style={{ fontSize: 26, fontWeight: 800, color: '#111', marginBottom: 0, letterSpacing: -0.5, lineHeight: 1.2 }}>{transfer.title}</h1>
            )}
          </div>

          {/* Card */}
          <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 4px 32px rgba(0,0,0,.08)', overflow: 'hidden' }}>
            {/* Message */}
            {transfer.message && (
              <div style={{ padding: '20px 28px', borderBottom: '1px solid #f0f0f0' }}>
                <p style={{ color: '#555', fontSize: 14, lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>"{transfer.message}"</p>
              </div>
            )}

            {/* Files list */}
            <div style={{ padding: '8px 0' }}>
              {transfer.files.map((file) => {
                const { bg, color, ext } = getMimeIcon(file.mime_type, file.file_name);
                const isHovered = hoveredFile === file.id;
                const isThisDownloading = downloadingId === file.id;
                return (
                  <div
                    key={file.id}
                    onMouseEnter={() => setHoveredFile(file.id)}
                    onMouseLeave={() => setHoveredFile(null)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      padding: '14px 28px',
                      background: isHovered ? '#fafafa' : '#fff',
                      transition: 'background .12s',
                      cursor: 'default',
                    }}
                  >
                    {/* File type badge */}
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 10, fontWeight: 800, color, letterSpacing: 0, textTransform: 'uppercase' }}>{ext.slice(0, 4)}</span>
                    </div>

                    {/* Name + size */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.file_name}</div>
                      <div style={{ fontSize: 12, color: '#aaa', marginTop: 1 }}>{formatFileSize(file.file_size)}</div>
                    </div>

                    {/* Per-file download (shows on hover) */}
                    <div style={{ opacity: isHovered ? 1 : 0, transition: 'opacity .15s', display: 'flex', gap: 6, flexShrink: 0 }}>
                      <a
                        href={file.proxy_url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          background: '#f3f4f6',
                          color: '#555',
                          textDecoration: 'none',
                          fontSize: 15,
                        }}
                        title="Görüntüle"
                      >
                        👁
                      </a>
                      <button
                        onClick={() => handleFileDownload(file)}
                        disabled={isThisDownloading}
                        title="İndir"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          background: '#111',
                          border: 'none',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: 14,
                        }}
                      >
                        {isThisDownloading ? (
                          <span
                            style={{
                              width: 14,
                              height: 14,
                              border: '2px solid rgba(255,255,255,.3)',
                              borderTopColor: '#fff',
                              borderRadius: '50%',
                              animation: 'wt-spin 0.7s linear infinite',
                              display: 'block',
                            }}
                          />
                        ) : (
                          '↓'
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: '#f0f0f0', margin: '0 28px' }} />

            {/* Summary + CTA */}
            <div style={{ padding: '20px 28px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <span style={{ fontSize: 13, color: '#888' }}>
                  {transfer.files.length} {transfer.files.length === 1 ? 'dosya' : 'dosya'} · {formatFileSize(totalSize)}
                </span>
                {expiryText && <span style={{ fontSize: 12, color: '#bbb' }}>{expiryText}</span>}
              </div>
              <button
                onClick={handleDownloadAll}
                disabled={downloadingAll}
                style={{
                  width: '100%',
                  padding: '16px',
                  borderRadius: 12,
                  border: 'none',
                  background: downloadingAll ? '#555' : '#111',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: downloadingAll ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  transition: 'background .2s',
                  letterSpacing: -0.2,
                }}
              >
                {downloadingAll ? (
                  <>
                    <span
                      style={{
                        width: 18,
                        height: 18,
                        border: '2px solid rgba(255,255,255,.3)',
                        borderTopColor: '#fff',
                        borderRadius: '50%',
                        animation: 'wt-spin 0.7s linear infinite',
                        display: 'block',
                        flexShrink: 0,
                      }}
                    />
                    Hazırlanıyor...
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    {transfer.files.length > 1 ? `${transfer.files.length} dosyayı indir` : 'Dosyayı indir'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={{ textAlign: 'center', padding: '0 0 28px', color: '#ccc', fontSize: 12 }}>
        <span style={{ fontWeight: 700, color: '#bbb' }}>AY-CRM</span> tarafından geliştirildi.
      </footer>
    </div>
  );
};

export default PublicTransferView;
